import { useEffect, useState } from "react";
import { api, buildLoginUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Rabbit, MessageSquare, Activity, Wifi } from "lucide-react";

export default function StatusBar() {
  const { me, refresh } = useAuth();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    const fetch = async () => {
      try {
        const { data } = await api.get("/status");
        if (alive) setStatus(data);
      } catch (e) { /* ignore */ }
    };
    fetch();
    const id = setInterval(fetch, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const restart = async () => {
    await api.post("/connections/restart");
    setTimeout(refresh, 1500);
  };
  const eventSubLabel = eventSubStatusLabel(status?.eventsub_status);

  return (
    <div className="sticky top-0 z-10 flex items-center gap-6 px-6 h-12 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-1))]/80 backdrop-blur" data-testid="status-bar">
      <div className="flex items-center gap-2 text-xs">
        <span className={`status-dot ${me?.stream_is_live ? "live" : "offline"}`} />
        <span className="label-eyebrow">{me?.stream_is_live ? "Stream Live" : "Offline"}</span>
      </div>
      <Sep />
      <div className="flex items-center gap-2" data-testid="streamer-identity">
        {me?.streamer?.profile_image_url && (
          <img src={me.streamer.profile_image_url} alt="" className="w-6 h-6 rounded-full" />
        )}
        <span className="text-sm font-medium">{me?.streamer?.display_name || "—"}</span>
        <span className="text-xs text-[hsl(var(--muted-foreground))]">@{me?.streamer?.twitch_login || me?.channel_login || "—"}</span>
      </div>
      <Sep />
      <Stat icon={MessageSquare} label="Chat" online={status?.chat_connected} />
      <Stat icon={Activity} label={eventSubLabel} online={status?.eventsub_connected} />
      <Stat icon={Wifi} label="Active chatters" value={status?.active_chatters ?? 0} />

      <div className="ml-auto flex items-center gap-3">
        {!me?.bot && (
          <a
            href={buildLoginUrl("bot")}
            target="_top"
            rel="noopener"
            data-testid="connect-bot-btn"
            className="text-xs uppercase tracking-widest px-3 py-1.5 rounded border border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
          >
            <Rabbit className="w-3 h-3 inline mr-1.5" /> Connect Bot Account
          </a>
        )}
        <button
          onClick={restart}
          data-testid="restart-connections-btn"
          className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] hover:text-white"
        >
          Restart Connections
        </button>
      </div>
    </div>
  );
}

function Sep() {
  return <div className="h-4 w-px bg-[hsl(var(--border))]" />;
}

function Stat({ icon: Icon, label = "", online, value } = {}) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {online !== undefined ? (
        <span className={`status-dot ${online ? "online" : "offline"}`} />
      ) : (
        <Icon className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
      )}
      <span className="label-eyebrow">{label}</span>
      {value !== undefined && <span className="font-mono text-white">{value}</span>}
    </div>
  );
}

function eventSubStatusLabel(status) {
  if (status === "webhook_ready") return "EventSub webhook";
  if (status === "websocket_connected") return "EventSub socket";
  if (status === "webhook_incomplete") return "EventSub setup";
  if (status === "websocket_waiting") return "EventSub waiting";
  if (status === "disabled") return "EventSub off";
  return "EventSub";
}
